import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, catchError, combineLatest, of, mergeMap, takeUntil, filter, first } from 'rxjs';
import { DataService } from 'src/app/shared/services/data.service';
import { PositionService } from 'src/app/shared/services/position.service';
import { AccountDetail } from '../models/account-detail';
import { faRotate } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-account-detail',
    templateUrl: './account-detail.component.html',
    styleUrls: ['./account-detail.component.scss'],
    standalone: false
})

export class AccountDetailComponent implements OnInit, OnDestroy {
    faRotate: any = faRotate;
    public accountDetails: AccountDetail[];
    public allElements: any[];
    public selectedDvpAccount: any;
    public activeTab = 0;

    public loading: boolean = true;
    private onDestroy$ = new Subject<void>();

    private currentRiaId: number;

    constructor(
        private dataService: DataService,
        private positionService: PositionService,
        private route: ActivatedRoute  // Inject ActivatedRoute
    ) { }

    ngOnInit(): void {
        const accountId = this.route.snapshot.params['AccountID'];
        this.loadAccountData(accountId);
    }

    private loadAccountData(accountId: string): void {
        this.loading = true;
        combineLatest([
            this.dataService.currentRiaId$.pipe(
                filter(riaId => riaId > 0), 
                first(),
                mergeMap(riaId => {
                    this.currentRiaId = riaId; 
                    return this.positionService.getAllDVPAccountsAndDetail(riaId);
                }))
        ])
            .pipe(
                takeUntil(this.onDestroy$), // Unsubscribe when the component is destroyed
                catchError((error) => {
                    console.error("Error fetching accounts:", error);
                    return of([]);
                })
            )
            .subscribe(
                ([elements]) => {
                    this.allElements = elements;
                    // Select an account based on the DVP account ID or default to the first element
                    const selectedElement = accountId ? this.allElements.find(el => el.accountId === accountId) : this.allElements[0];
                    this.ChangeSelection(selectedElement);  // Ensure ChangeSelection can handle undefined/null gracefully
                    this.loading = false;
                }
            );
    }

    public onChange(event: Event): void {
        // [(ngModel)] already updates selectedDvpAccount before (change) fires
        this.ChangeSelection(this.selectedDvpAccount);
    }

    public compareAccounts(a: any, b: any): boolean {
        return a && b && a.accountId === b.accountId;
    }

    public ChangeSelection(value) {
        this.selectedDvpAccount = value;
        this.positionService.accountPkChangedSubject
            .next(this.selectedDvpAccount.accountPk);
        this.accountDetails = [
            { description: "Beginning of Day Buying Power", value: this.selectedDvpAccount.bodBuyPower },
            { description: "Current Buying Power *", value: this.selectedDvpAccount.curBuyPower }
        ];
    }

    public refreshData(): void {
        if (this.loading) return;
        this.loading = true;
        
        // Store current selection to restore after refresh
        const currentAccountId = this.selectedDvpAccount?.accountId;
        
        combineLatest([
            this.dataService.currentRiaId$.pipe(
                filter(riaId => riaId > 0), 
                first(),
                mergeMap(riaId => {
                    this.currentRiaId = riaId; 
                    return this.positionService.getAllDVPAccountsAndDetail(riaId);
                }))
        ])
            .pipe(
                takeUntil(this.onDestroy$),
                catchError((error) => {
                    console.error("Error fetching accounts:", error);
                    return of([]);
                })
            )
            .subscribe(
                ([elements]) => {
                    this.allElements = elements;
                    // Restore the previously selected account, or use route param, or default to first
                    const selectedElement = currentAccountId 
                        ? this.allElements.find(el => el.accountId === currentAccountId)
                        : (this.route.snapshot.params['AccountID'] 
                            ? this.allElements.find(el => el.accountId === this.route.snapshot.params['AccountID'])
                            : this.allElements[0]);
                    this.ChangeSelection(selectedElement);
                    this.loading = false;
                }
            );
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
